import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';
import dayjs from 'dayjs';
import { useTourMode } from '../../../../contexts/TourModeContext';

export interface PriceRecord1Min {
  date: string;
  open: number;
  low: number;
  high: number;
  close: number;
  volume: number;
}

export interface PriceDataResponse1Min {
  data: PriceRecord1Min[];
  metadata: {
    requestedDate: string;
    actualDate: string;
    fallbackUsed: boolean;
    recordCount: number;
  };
}

export interface PriceDataWithMetadata1Min {
  data: PriceRecord1Min[];
  requestedDate: string;
  actualDate: string;
  fallbackUsed: boolean;
  recordCount: number;
}

interface Price1MinMap {
  [key: string]: PriceDataWithMetadata1Min;
}

const getMostRecentTradingDay = (): string => {
  const today = dayjs();
  const dayOfWeek = today.day(); // 0 = Sunday, 6 = Saturday
  
  // If it's Saturday (6), go back to Friday
  if (dayOfWeek === 6) {
    return today.subtract(1, 'day').format('YYYY-MM-DD');
  }
  
  // If it's Sunday (0), go back to Friday
  if (dayOfWeek === 0) {
    return today.subtract(2, 'days').format('YYYY-MM-DD');
  }
  
  // Otherwise, use today (Monday-Friday)
  return today.format('YYYY-MM-DD');
};

export const useCompanyPrice1Min = (
  ticker: string | undefined,
  tradingDate?: string,
  // Opt-in polling for always-visible surfaces (the header index strip).
  // Default stays fetch-on-mount only — dashboard mini charts don't poll.
  refetchIntervalMs?: number
) => {
  const { isTourMode, getMockData } = useTourMode();
  const effectiveTradingDate = tradingDate || getMostRecentTradingDay();

  return useQuery<PriceDataWithMetadata1Min, Error>({
    queryKey: ['companyPrice1Min', ticker, effectiveTradingDate, isTourMode],
    queryFn: async () => {
      if (!ticker) {
        throw new Error('Ticker is required');
      }

      // In tour mode, return mock data for the ticker
      if (isTourMode) {
        const priceData = getMockData<Price1MinMap>('price1Min');
        if (priceData?.[ticker]) {
          return priceData[ticker];
        }
        // Return empty data if no mock data for this ticker
        return {
          data: [],
          requestedDate: effectiveTradingDate,
          actualDate: effectiveTradingDate,
          fallbackUsed: false,
          recordCount: 0,
        };
      }

      const params: any = {
        ticker,
        from: effectiveTradingDate,
        to: effectiveTradingDate,
      };

      const { data: response } = await api.get<PriceDataResponse1Min>('/company-price-1min', {
        params,
      });

      // Transform the response to a flatter structure for easier consumption
      return {
        data: response.data,
        requestedDate: response.metadata.requestedDate,
        actualDate: response.metadata.actualDate,
        fallbackUsed: response.metadata.fallbackUsed,
        recordCount: response.metadata.recordCount,
      };
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    refetchInterval: isTourMode ? false : (refetchIntervalMs ?? false),
    staleTime: isTourMode ? Infinity : 15 * 1000, // Never refetch in tour mode
  });
};